import React, { useState } from 'react';
import { Download } from 'lucide-react';
import Button from '@components/common/Button';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { auditService } from '@services/index';
import { showToast } from '@utils/toast';
import { toLocalDateInput } from '@utils/dateUtils';
import styles from './AuditLogExport.module.css';

function getDefaultStartDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function getDefaultEndDate(): string {
  // Local day, not the UTC day: late in the evening the UTC date can already be tomorrow
  return toLocalDateInput();
}

const AuditLogExport: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  const handleGenerateAuditLog = async () => {
    setIsGenerating(true);
    try {
      await auditService.exportAuditLog(startDate, endDate);
      showToast.success('Audit log downloaded successfully');
    } catch {
      showToast.error('Failed to generate audit log. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.container} data-test-id="audit-log-settings">
      <SettingsSectionHeader
        title="Audit Log"
        description="Generate and download audit log events as an Excel file. The export includes timestamps, users, actions, and field-level changes across all entities."
      />
      <div className={styles.filterRow}>
        <div className={styles.field}>
          <label htmlFor="audit-start-date" className={styles.label}>Start Date</label>
          <input
            id="audit-start-date"
            type="date"
            className={styles.dateInput}
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="audit-end-date" className={styles.label}>End Date</label>
          <input
            id="audit-end-date"
            type="date"
            className={styles.dateInput}
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          variant="primary"
          onClick={handleGenerateAuditLog}
          loading={isGenerating}
          loadingText="Generating..."
          dataTestId="btn-generate-audit-log"
        >
          <Download size={16} />
          Generate Audit Log
        </Button>
      </div>
    </div>
  );
};

export { AuditLogExport };
